import { Icon } from '@driftt/tide-icons'
import { Subnav as SideNav } from '@driftt/tide-subnav'
import cx from 'classnames'
import produce from 'immer'
import qs from 'qs'
import { any, merge, path, pathOr, pick } from 'ramda'
import React, { Component, Fragment, lazy, Suspense } from 'react'
import { NavLink } from 'react-router-dom'
import { Redirect, Route, Switch } from 'react-router-dom'

import { getConnectedCalendarIds } from 'api/calendar'
import { getTeams } from 'api/org'
import {
  checkPlaybook,
  getLandingPageCname,
  getLandingPageIntegrations,
  getLandingPageSettings,
  getLandingPageSubdomain,
  getPlaybookVersionAt,
  saveDraft,
} from 'api/playbooks'
import {
  changeAITopicsEnabledStatus,
  getAITopicsEnabled,
} from 'api/topicTraining'
import EditorNavigation from 'components/EditorNavigation'
import PlaybookStatusBar from 'components/PlaybookStatusBar'
import TransientBanner from 'components/TransientBanner'
import UniversalLoader from 'components/UniversalLoader'
import { SETTINGS_KEYS } from 'constants/clpSettings'
import { BOT_FLOW_MODES } from 'modules/editor/reducer'
import { pause } from 'utils'
import { isEmptyOrNil } from 'utils'
import {
  getWorkspacesWithWritablePlaybooks,
  userHasAnyPlaybookAccess,
  userHasChatPlaybooks,
  userHasFastlanePlaybooks,
} from 'utils/access'
import { isPlaybookDirty } from 'utils/comparer'
import { getSubNavNodes } from 'utils/getSubNavNodes'
import { fromNow } from 'utils/moment'
import {
  hasLegacyPlaybookView,
  isChatPlaybook,
  isFastLanePlaybook,
  isMigratedPlaybook,
} from 'utils/playbooks'
import {
  trackAttemptsToEditPlaybookInPreviewState,
  trackClickedOnPreviewPlaybook,
  trackExperimentBotFlowDeleted,
  trackExperimentCreated,
  trackPlaybookReverted,
  trackPlaybookUpdate,
  trackPlaybookValidationFailure,
} from 'utils/tracking'

import './style.css'

const LazyAudience = lazy(() => import('containers/Audience'))
const LazyBotPage = lazy(() => import('containers/BotPage'))
const LazyBotTest = lazy(() => import('components/BotTest'))
const LazyCLPDesign = lazy(() => import('components/ClpDesign'))
const LazyCLPShare = lazy(() => import('components/ClpShare'))
const LazyDiff = lazy(() => import('components/Diff'))
const LazyEditor = lazy(() => import('components/EditorContext'))
const LazySettings = lazy(() => import('containers/Settings'))
const LazyLegacySettings = lazy(() => import('components/LegacySettings'))
const LazyLegacyEditor = lazy(() => import('components/LegacyEditor'))
const LazyTargeting = lazy(() => import('components/Targeting'))
const LazySummary = lazy(() => import('components/Summary'))
const LazyFLFormSetup = lazy(() =>
  import('components/Fastlane/FLFormSetup').then((module) => {
    return { default: module.FLFormSetup }
  })
)
const LazyFLInstall = lazy(() =>
  import('components/Fastlane/FLInstall/FLInstall')
)
const LazyNotFound = lazy(() => import('components/NotFound'))

const Loader = () => (
  <UniversalLoader
    className="playbook-editor-loading"
    loaderText="Loading your playbook data…"
  />
)

class BotEditorLayout extends Component {
  constructor(props) {
    super(props)

    this.state = {
      hasFileSizeError: false,
      failed: false,
      loading: false,
      playbook: props.playbook || {},
      publishedPlaybook: props.publishedPlaybook || {},
      selectedPlaybookVersion: null,
      playbookVersionAuthor: '',
      versionTimestamps: {},
      autoSaving: false,
      showTransientBanner: false,
      transientStatePayload: {},
      isAIEnabled: false,
    }
  }

  componentDidMount() {
    const { playbook } = this.state
    const canWritePlaybookInWorkspace = getWorkspacesWithWritablePlaybooks(
      playbook
    ).includes(playbook.workspaceId)

    const hasPlaybookAccess =
      (isFastLanePlaybook(playbook) && userHasFastlanePlaybooks()) ||
      (isChatPlaybook(playbook.reportType) && userHasChatPlaybooks())

    if (!hasPlaybookAccess && !canWritePlaybookInWorkspace) {
      window.location.href = '/playbooks'
      return
    }

    this.fetchLandingPage()
    this.fetchLandingPageSubdomain()
    this.fetchLandingPageIntegrations()
    this.fetchLandingPageCname()
    this.fetchPlaybookVersionFromURL()
    this.fetchAITopicsStatus()
    this.fetchConnectedCalendars()
  }

  fetchPlaybookVersionFromURL = async () => {
    const { match, location, showTransientNotification } = this.props
    const queryParams = qs.parse(location.search.slice(1))

    if (queryParams.versionAt) {
      try {
        const { data } = await getPlaybookVersionAt(
          match.params.id,
          queryParams.versionAt
        )
        this.updateSelectedPlaybookVersion(data)
      } catch (err) {
        console.warn(err)
        showTransientNotification({
          type: 'error',
          message: "We couldn't find that playbook at that timestamp.",
        })
      }
    }
  }

  fetchLandingPageCname = async () => {
    try {
      const { data } = await getLandingPageCname()
      const { onReceiveLandingPageCname } = this.props
      onReceiveLandingPageCname(data)
    } catch (err) {
      console.warn(err)
    }
  }

  fetchLandingPageIntegrations = async () => {
    try {
      const { data } = await getLandingPageIntegrations()
      const { onReceiveLandingPageIntegrations } = this.props
      onReceiveLandingPageIntegrations(data)
    } catch (err) {
      console.warn(err)
    }
  }

  fetchLandingPageSubdomain = async () => {
    try {
      const { data } = await getLandingPageSubdomain()
      const { onReceiveLandingPageSubdomain } = this.props
      onReceiveLandingPageSubdomain(data.subdomain)
    } catch (err) {
      console.warn(err)
    }
  }

  fetchAITopicsStatus = async () => {
    const { playbook } = this.state

    try {
      const { data: settingsResponse } = await getAITopicsEnabled(
        playbook.id,
        playbook.orgId
      )
      this.setState({
        isAIEnabled: settingsResponse.enabled,
      })
    } catch {
      console.log('error fetching status')
    }
  }

  fetchLandingPage = async () => {
    const { match } = this.props
    try {
      const { data } = await getLandingPageSettings(match.params.id)
      const { onReceiveLandingPages } = this.props
      onReceiveLandingPages(data)
    } catch (err) {
      console.warn(err)
    }
  }

  fetchConnectedCalendars = async () => {
    try {
      const { data = [] } = await getConnectedCalendarIds()
      this.setState({ connectedCalendarIds: data })
    } catch (err) {
      console.warn(err)
    }
  }

  getOrgTeams = async () => {
    try {
      const { data } = await getTeams()
      this.setState({
        orgTeams: data,
      })
    } catch (err) {
      console.error(err)
    }
  }

  autoSave = async () => {
    const {
      currentUser,
      isPreviewState,
      isExperimentEnabled,
      onReceivePlaybook,
      onValidatePlaybook,
      showTransientNotification,
    } = this.props
    const { playbook } = this.state

    if (isPreviewState || isExperimentEnabled) {
      return this.preventUpdate(false)
    }

    const setLoaded = async () => {
      await pause(500)
      this.setState({
        autoSaving: false,
      })
    }

    const doCall = async () => {
      try {
        const draftPlaybook = { ...playbook, updatedAuthorId: currentUser.id }
        const { data } = await saveDraft({ playbook: draftPlaybook })
        trackPlaybookUpdate()
        onReceivePlaybook(data)
        const { blocksById } = this.props
        onValidatePlaybook(
          data,
          blocksById,
          this.state.connectedCalendarIds,
          this.state.orgTeams
        )
        setLoaded()
      } catch (err) {
        showTransientNotification({
          type: 'error',
          message: `Could not save draft.`,
        })

        if (err?.response?.status === 413) {
          this.setState({
            hasFileSizeError: true,
          })
        }

        setLoaded()
      }
    }

    this.setState(
      {
        autoSaving: true,
        hasFileSizeError: false,
      },
      doCall
    )
  }

  cancelPreviewState = () => {
    const { onSetPreviewState, removeGlobalNotification } = this.props

    this.setState(
      () => ({
        selectedPlaybookVersion: null,
        playbookVersionAuthor: '',
        versionTimestamps: {},
      }),
      onSetPreviewState(false)
    )

    removeGlobalNotification(0)
  }

  handleTransientState = ({ showTransientBanner, transientStatePayload }) => {
    this.setState(
      produce((draft) => {
        draft.showTransientBanner = showTransientBanner
        draft.transientStatePayload = transientStatePayload
      })
    )
  }

  createPlaybookEditorHandler =
    ({ isExperiment } = {}) =>
    ({ playbook: updatedPlaybook, classifications }) => {
      const { isPreviewState, isExperimentEnabled, onReceiveClassifications } =
        this.props

      if (isPreviewState || isExperimentEnabled) return this.preventUpdate()

      this.setState(
        produce((draft) => {
          const draftPlaybook = isExperiment
            ? draft.playbook.experiment
            : draft.playbook

          draftPlaybook.actions[0].action = updatedPlaybook.actions[0].action
          draftPlaybook.goals = updatedPlaybook.goals
          draftPlaybook.actionGroups = updatedPlaybook.actionGroups
        }),
        this.verifyChanges
      )
      onReceiveClassifications(classifications)
    }

  preventUpdate = (showNotification = true) => {
    const { showTransientNotification } = this.props
    trackAttemptsToEditPlaybookInPreviewState()
    showNotification &&
      showTransientNotification({
        type: 'info',
        message: 'Changes will not be saved while previewing playbook',
      })
  }

  updateTargetingAdvanced = ({ targeting }) => {
    const { isPreviewState, isExperimentEnabled } = this.props

    if (isPreviewState || isExperimentEnabled) return this.preventUpdate()

    this.setState(
      produce(({ playbook }) => {
        playbook.targeting.campaignReenrollmentType =
          targeting.campaignReenrollmentType
      }),
      this.verifyChanges
    )
  }

  updateTargetingInboxId = (inboxId) => {
    const { isPreviewState, isExperimentEnabled } = this.props

    if (isPreviewState || isExperimentEnabled) return this.preventUpdate()

    this.setState(
      produce(({ playbook }) => {
        playbook.targeting.inboxId = inboxId
      }),
      this.verifyChanges
    )
  }

  // Legacy playbooks use the inboxId within the action object
  updateActionInboxId = (inboxId) => {
    const { isPreviewState, isExperimentEnabled } = this.props

    if (isPreviewState || isExperimentEnabled) return this.preventUpdate()

    this.setState(
      produce(({ playbook }) => {
        playbook.actions[0].action.inboxId = inboxId
      }),
      this.verifyChanges
    )
  }

  handleCreateExperiment = ({ playbook: experimentPlaybook } = {}) => {
    trackExperimentCreated()
    this.setState(
      produce(({ playbook }) => {
        playbook.experiment = pick(
          ['actions', 'actionGroups', 'goals'],
          experimentPlaybook || playbook
        )
        playbook.experiment.enabled = false
      }),
      () => {
        const { history } = this.props
        const { playbook } = this.state
        this.verifyChanges()
        history.push(`/editor/${playbook.id}/visual-builder/experiment`)
      }
    )
  }

  handleDeleteExperiment = ({ useVariant = false }) => {
    trackExperimentBotFlowDeleted({ flow: useVariant ? 'A' : 'B' })
    this.setState(
      produce(({ playbook }) => {
        if (useVariant) {
          playbook.actions = playbook.experiment.actions
          playbook.actionGroups = playbook.experiment.actionGroups
          playbook.goals = playbook.experiment.goals
        }
        delete playbook.experiment
      }),
      this.verifyChanges
    )
  }

  updateAudience = (audience) => {
    const { isPreviewState, isExperimentEnabled } = this.props

    if (isPreviewState || isExperimentEnabled) return this.preventUpdate()

    this.setState(
      produce(({ playbook }) => {
        playbook.targeting = merge(playbook.targeting, audience)
      }),
      this.verifyChanges
    )
  }

  updateDisplayConditions = ({ targeting, meta }) => {
    const { isPreviewState, isExperimentEnabled } = this.props
    if (isPreviewState || isExperimentEnabled) return this.preventUpdate()

    this.setState(
      produce(({ playbook }) => {
        playbook.targeting = merge(playbook.targeting, targeting)
        playbook.meta = merge(playbook.meta, meta)
      }),
      this.verifyChanges
    )
  }

  updateDriftLink = ({ field, value }) => {
    const { isPreviewState, isExperimentEnabled } = this.props

    if (isPreviewState || isExperimentEnabled) return this.preventUpdate()

    function nonDriftlinkAudience({ targeting = {} }) {
      let hasSmartFilters = false
      const accountListIds = pathOr([], ['accountListIds'], targeting)
      const smartFilters = pathOr({}, ['smartFilters'], targeting)
      const userListIds = pathOr([], ['userListIds'], targeting)

      for (const filter in smartFilters) {
        const currentFilter = smartFilters[filter] || null

        if (currentFilter === 'INCLUDE') {
          hasSmartFilters = true
        }
      }

      return userListIds.length > 0 ||
        accountListIds.length > 0 ||
        hasSmartFilters
        ? 'SEGMENT'
        : 'EVERYONE'
    }

    this.setState(
      produce(({ playbook }) => {
        const updaters = {
          DRIFT_LINK: (value) => {
            playbook.meta.driftLinkOnly = value
            playbook.targeting.audience = value
              ? 'NONE'
              : nonDriftlinkAudience(playbook)
          },
          SLUG: (value) => {
            playbook.actions[0].action.slug = value
          },
        }

        updaters[field](value)
      }),
      this.verifyChanges
    )
  }

  updatePlaybookAfterPublish = (playbook) => {
    this.setState(
      {
        playbook: playbook,
        publishedPlaybook: playbook,
      },
      this.autoSave
    )
  }

  updatePlaybookAfterStatusChange = (playbook) => {
    const { isPreviewState } = this.props

    if (isPreviewState) return this.preventUpdate()

    this.setState({
      publishedPlaybook: playbook,
    })
  }

  verifyChanges = async () => {
    const { playbook } = this.state
    const { showTransientNotification } = this.props

    const hasEncodedImageError = (responseData) => {
      const encodedImageError = (error) =>
        error.type === 'ENCODED_IMAGE_PRESENT'
      return !isEmptyOrNil(responseData) && any(encodedImageError)(responseData)
    }

    try {
      await checkPlaybook(playbook)
      return this.autoSave()
    } catch (e) {
      trackPlaybookValidationFailure(playbook.id)
      if (e.response.status === 409) {
        showTransientNotification({
          type: 'error',
          message:
            'Oops! This Driftlink is already used by a different playbook.',
        })
      } else if (
        e.response.status === 400 &&
        e.response.data.message?.toLowerCase().includes('ticket creation skill')
      ) {
        showTransientNotification({
          type: 'error',
          message:
            'Oops! Before ticket creation is usable, please complete your ticket management integration in "Integrations" and "Bot Settings"',
        })
      } else if (
        e.response.status === 400 &&
        hasEncodedImageError(e.response.data)
      ) {
        showTransientNotification({
          type: 'error',
          message:
            'Invalid image in one or more of the skills. Please remove and re-upload.',
        })
      } else {
        showTransientNotification({
          type: 'error',
          message:
            'Oops! Something went wrong that will prevent this playbook from being saved. Please undo your last action.',
        })
      }
    }
  }
  changeEnabledStatus = async () => {
    const { playbook, isAIEnabled } = this.state
    const { showTransientNotification } = this.props

    try {
      await changeAITopicsEnabledStatus(
        playbook.id,
        playbook.orgId,
        !isAIEnabled
      )
      this.setState({
        isAIEnabled: !isAIEnabled,
      })
      this.setState(
        produce(({ playbook }) => {
          playbook.meta.aiEnabled = !isAIEnabled
        })
      )
    } catch {
      showTransientNotification({
        type: 'error',
        message: 'Error saving settings',
      })
    }
  }

  revertPlaybook = async (playbook) => {
    const { isPreviewState, isExperimentEnabled } = this.props

    const triggerLoaded = async () => {
      await pause(1000)

      this.setState(
        {
          loading: false,
        },
        async () => {
          trackPlaybookReverted()

          if (isPreviewState || isExperimentEnabled) {
            this.cancelPreviewState()
            await pause(100)
            this.autoSave()
          }
        }
      )
    }

    this.setState(
      () => ({
        playbook,
        loading: true,
      }),
      triggerLoaded
    )
  }

  displayPreviewNotification = () => {
    const { removeGlobalNotification, showGlobalNotification } = this.props
    const { playbookVersionAuthor, versionTimestamps } = this.state

    const showNotification = async () => {
      await pause(2)

      showGlobalNotification({
        className: 'preview-state-notification',
        body: (
          <div className="enable-notification-banner-body">
            <span>
              <Icon width={18} name="notification-active" />
              Currently viewing the playbook version published by{' '}
              {playbookVersionAuthor}{' '}
              {fromNow(versionTimestamps.retiredTimestamp)}
            </span>
          </div>
        ),
        type: 'enableNotifications',
        actions: [
          {
            title: 'Cancel',
            action: this.cancelPreviewState,
          },
        ],
      })
    }

    removeGlobalNotification(0)
    showNotification()
  }

  updateSelectedPlaybookVersion = ({
    createdTimestamp,
    playbook,
    playbookAuthor,
    retiredTimestamp,
  }) => {
    this.setState(
      () => ({
        selectedPlaybookVersion: playbook,
        playbookVersionAuthor: playbookAuthor,
        versionTimestamps: {
          createdTimestamp,
          retiredTimestamp,
        },
      }),
      () => {
        trackClickedOnPreviewPlaybook()
        this.props.onSetPreviewState(true)
        this.displayPreviewNotification()
      }
    )
  }

  getDefaultLink = () => {
    const { match, isFastlane, isFastlaneQuickSetup } = this.props
    const { playbook } = this.state

    const isMigratedType = hasLegacyPlaybookView(playbook?.reportType)

    let prefix = `/editor/${match.params.id}`

    if (isFastlane) {
      if (isFastlaneQuickSetup) {
        return `${prefix}/form-setup`
      }
      return `${prefix}/install`
    }

    if (isMigratedType) {
      return `${prefix}/bot-message`
    }

    return `${prefix}/visual-builder`
  }

  toggleSidebar = () => {
    const { navSideBarOpen } = this.state
    this.setState({
      navSideBarOpen: !navSideBarOpen,
    })
  }

  renderPlaybookNavHeader = () => {
    const {
      autoSaving,
      playbook,
      publishedPlaybook,
      selectedPlaybookVersion,
      versionTimestamps,
      knownInvalidPlaybook,
      isAIEnabled,
    } = this.state
    const {
      isPreviewState,
      history,
      location,
      match,
      orgProperties: { clpDomain, widgetInstalled },
      trackPublishAndResetTimer,
      isFastlane,
      isFastlaneQuickSetup,
      isMeetingBooking,
      playbookReportType,
    } = this.props
    const renderedPlaybook =
      isPreviewState && selectedPlaybookVersion
        ? selectedPlaybookVersion
        : playbook

    const navigationProps = {
      autoSaving,
      cancelPreview: this.cancelPreviewState,
      draftPlaybook: playbook,
      id: match.params.id,
      isDriftLinkOnly: pathOr(
        false,
        ['meta', 'driftLinkOnly'],
        renderedPlaybook
      ),
      isPreviewState,
      history,
      location: location,
      knownInvalidPlaybook,
      publishedPlaybook,
      revertPlaybook: this.revertPlaybook,
      selectedPlaybookVersion,
      versionTimestamps,
      targeting: path(['targeting'], renderedPlaybook),
      updatePlaybookAfterPublish: this.updatePlaybookAfterPublish,
      updatePlaybookAfterStatusChange: this.updatePlaybookAfterStatusChange,
      updateSelectedPlaybookVersion: this.updateSelectedPlaybookVersion,
      clpDomain,
      widgetInstalled,
      onCreateExperiment: this.handleCreateExperiment,
      onDeleteExperiment: this.handleDeleteExperiment,
      onChangeAIEnabled: this.changeEnabledStatus,
      isAIEnabled: isAIEnabled,
      trackPublishAndResetTimer,
      isFastlane,
      isFastlaneQuickSetup,
      isMeetingBooking,
      playbookReportType,
    }

    return <EditorNavigation {...navigationProps} />
  }

  render() {
    const {
      failed,
      loading,
      playbook,
      autoSaving,
      publishedPlaybook,
      selectedPlaybookVersion,
      showTransientBanner,
      transientStatePayload,
      isAIEnabled,
      hasFileSizeError,
      navSideBarOpen,
    } = this.state
    const {
      isPreviewState,
      type,
      orgProperties: { clpDomain, widgetInstalled = false },
      playbookErrors,
      trackPublishAndResetTimer,
      outdatedBlocks,
      botFlowMode,
      isFastlane: isFastLaneExperiencePlaybook,
      isFastlaneQuickSetup: isQuickSetupPlaybook,
    } = this.props

    const isDirty = isPlaybookDirty(playbook, publishedPlaybook)
    const hasActiveExperiment = !!playbook.experiment

    const hasFastlaneAccess = userHasFastlanePlaybooks()

    const defaultLink = this.getDefaultLink()

    const renderedPlaybook =
      isPreviewState && selectedPlaybookVersion
        ? selectedPlaybookVersion
        : playbook
    const renderedPublishedPlaybook =
      isPreviewState && selectedPlaybookVersion
        ? selectedPlaybookVersion
        : publishedPlaybook

    if (failed) return <Redirect to="/not-found" />
    if (loading) {
      return <Loader />
    }

    const isPublishedPlaybookEnabled =
      renderedPublishedPlaybook.status === 'ENABLED'
    const hasAnyPlaybookAccess = userHasAnyPlaybookAccess()

    const SUB_NAV_NODES = getSubNavNodes()

    return (
      <Fragment>
        {showTransientBanner && (
          <TransientBanner payload={transientStatePayload} />
        )}
        {hasAnyPlaybookAccess && (
          <SideNav
            open={navSideBarOpen}
            onToggle={this.toggleSidebar}
            title="Chatbot"
            nodes={SUB_NAV_NODES}
            NavLink={NavLink}
          />
        )}
        <PlaybookStatusBar
          publishedPlaybook={publishedPlaybook}
          playbook={playbook}
          playbookErrors={playbookErrors}
          isPlaybookDirty={isDirty}
          isSaving={autoSaving}
          isPreviewState={isPreviewState}
          onPostPublish={this.updatePlaybookAfterPublish}
          trackPublishAndResetTimer={trackPublishAndResetTimer}
          updateStatus={this.updatePlaybookAfterStatusChange}
          hasFileSizeError={hasFileSizeError}
        />
        <div className="playbook-editor-layout">
          {this.renderPlaybookNavHeader()}
          <section
            className={cx('playbook-editor-content-area', {
              'content-review-mode-content-area':
                botFlowMode === BOT_FLOW_MODES.CONTENT_VIEW,
            })}
          >
            <Suspense fallback={<Loader />}>
              <Switch>
                <Route
                  exact
                  path="/editor/:id"
                  render={() => <Redirect to={defaultLink} />}
                />
                <Route
                  exact
                  path="/editor/:id/diff"
                  render={({ location }) => (
                    <LazyDiff
                      key={location.pathName}
                      playbook={renderedPlaybook}
                    />
                  )}
                />
                <Route
                  path="/editor/:id/visual-builder/experiment"
                  render={({ location }) => (
                    <LazyEditor
                      key={location.pathname}
                      isPreviewState={isPreviewState}
                      type={type}
                      title="Start of Bot Flow B"
                      onChange={this.createPlaybookEditorHandler({
                        isExperiment: true,
                      })}
                      onTransientChange={this.handleTransientState}
                      playbook={{
                        ...renderedPlaybook.experiment,
                        id: renderedPlaybook.id,
                      }}
                      isAIEnabled={isAIEnabled}
                      botFlowMode={botFlowMode}
                    />
                  )}
                />

                <Route
                  path="/editor/:id/visual-builder"
                  render={({ location }) => (
                    <LazyEditor
                      key={location.pathname + hasActiveExperiment}
                      isPreviewState={isPreviewState}
                      type={type}
                      title={`Start of Bot Flow${
                        hasActiveExperiment ? ' A' : ''
                      }`}
                      onChange={this.createPlaybookEditorHandler()}
                      onCreateExperiment={this.handleCreateExperiment}
                      onTransientChange={this.handleTransientState}
                      playbook={renderedPlaybook}
                      isAIEnabled={isAIEnabled}
                      outdatedBlocks={outdatedBlocks}
                      botFlowMode={botFlowMode}
                    />
                  )}
                />

                <Route
                  path="/editor/:id/bot-message"
                  render={() => (
                    <LazyLegacyEditor
                      onChange={this.createPlaybookEditorHandler()}
                      playbook={renderedPlaybook}
                    />
                  )}
                />

                <Route
                  exact
                  path="/editor/:id/targeting/builder"
                  render={() => (
                    <LazyAudience
                      isPlaybookEnabled={isPublishedPlaybookEnabled}
                      onChange={this.updateDisplayConditions}
                      playbook={renderedPlaybook}
                      widgetInstalled={widgetInstalled}
                      isPreviewState={isPreviewState}
                      isQuickSetupPlaybook={isQuickSetupPlaybook}
                    />
                  )}
                />

                <Route
                  path="/editor/:id/targeting/drift-link"
                  render={() => (
                    <LazyTargeting
                      onChange={this.updateDriftLink}
                      playbook={renderedPlaybook}
                    />
                  )}
                />

                <Route
                  path="/editor/:id/summary"
                  render={() => <LazySummary playbook={renderedPlaybook} />}
                />

                <Route
                  path="/editor/:id/settings"
                  render={() => {
                    if (isMigratedPlaybook(renderedPlaybook.reportType)) {
                      return (
                        <LazyLegacySettings
                          updateActionInboxId={this.updateActionInboxId}
                        />
                      )
                    }

                    return (
                      <LazySettings
                        inboxId={renderedPlaybook.targeting.inboxId}
                        updateTargetingInboxId={this.updateTargetingInboxId}
                        id={renderedPlaybook.id}
                        isPlaybookEnabled={isPublishedPlaybookEnabled}
                      />
                    )
                  }}
                />

                <Route
                  path="/editor/:id/targeting/bot-page"
                  render={(props) => (
                    <LazyBotPage
                      isPreviewState={isPreviewState}
                      onChange={this.updateTargetingInboxId}
                      preventUpdate={this.preventUpdate}
                      playbook={renderedPlaybook}
                      onDisplayChange={this.updateDisplayConditions}
                      updatePlaybookAfterStatusChange={
                        this.updatePlaybookAfterStatusChange
                      }
                      domainSelection={clpDomain}
                      {...props}
                    />
                  )}
                />

                <Route
                  path="/editor/:id/test"
                  render={(props) => (
                    <LazyBotTest
                      isPreviewState={isPreviewState}
                      onDisplayChange={this.updateDisplayConditions}
                      onChange={this.updateTargetingInboxId}
                      preventUpdate={this.preventUpdate}
                      playbook={renderedPlaybook}
                      updatePlaybookAfterStatusChange={
                        this.updatePlaybookAfterStatusChange
                      }
                      domainSelection={clpDomain}
                      {...props}
                    />
                  )}
                />

                <Route
                  path="/editor/:id/clp/styles"
                  render={(props) => (
                    <LazyCLPDesign
                      isPreviewState={isPreviewState}
                      onChange={() => {}}
                      preventUpdate={this.preventUpdate}
                      publishedPlaybook={renderedPublishedPlaybook}
                      updatePlaybookAfterStatusChange={
                        this.updatePlaybookAfterStatusChange
                      }
                      pageKey={SETTINGS_KEYS.STYLES}
                      {...props}
                    />
                  )}
                />

                <Route
                  path="/editor/:id/clp/content"
                  render={(props) => (
                    <LazyCLPDesign
                      isPreviewState={isPreviewState}
                      onChange={() => {}}
                      preventUpdate={this.preventUpdate}
                      publishedPlaybook={renderedPublishedPlaybook}
                      updatePlaybookAfterStatusChange={
                        this.updatePlaybookAfterStatusChange
                      }
                      pageKey={SETTINGS_KEYS.CONTENT}
                      {...props}
                    />
                  )}
                />

                <Route
                  path="/editor/:id/clp/share"
                  render={(props) => (
                    <LazyCLPShare
                      isPreviewState={isPreviewState}
                      onChange={this.updateTargetingInboxId}
                      preventUpdate={this.preventUpdate}
                      publishedPlaybook={renderedPublishedPlaybook}
                      updatePlaybookAfterStatusChange={
                        this.updatePlaybookAfterStatusChange
                      }
                      domainSelection={clpDomain}
                      {...props}
                    />
                  )}
                />

                {hasFastlaneAccess && isFastLaneExperiencePlaybook && (
                  <>
                    <Route
                      path="/editor/:id/form-mapping"
                      render={() => (
                        <LazyFLFormSetup playbook={renderedPlaybook} />
                      )}
                    />
                    <Route
                      path="/editor/:id/form-setup"
                      render={() => (
                        <LazyFLFormSetup playbook={renderedPlaybook} />
                      )}
                    />
                    <Route
                      path="/editor/:id/install"
                      render={() => (
                        <LazyFLInstall playbook={renderedPlaybook} />
                      )}
                    />
                  </>
                )}

                <Route render={() => <LazyNotFound />} />
              </Switch>
            </Suspense>
          </section>
        </div>
      </Fragment>
    )
  }
}

export default BotEditorLayout
